import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { Button } from "src/domains/shared/components/Button";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import {
  CAPTION,
  ERROR_SURFACE,
  INFO_SURFACE,
  NEUTRAL_SURFACE,
} from "./stateTransitionStyles";
import { LineGlyph, LineGlyphName } from "./LineGlyph";

export type StateSlotTone = "error" | "info" | "neutral";

/** `compact` 는 메뉴·팝오버 본문처럼 작은 상자 안의 상태 칸이다(아이콘 원 28px · 제목 12px). */
export type StateSlotDensity = "regular" | "compact";

export interface StateSlotProps {
  tone: StateSlotTone;
  icon: LineGlyphName;
  title: string;
  description?: string;
  retryLabel?: string;
  onRetry?: () => void;
  density?: StateSlotDensity;
}

const DENSITY_STYLE: Record<
  StateSlotDensity,
  { gap: string; padding: string; circle: number; glyph: number; titleSize: number; titleLineHeight: string; retryGlyph: number; retryGap: string }
> = {
  regular: { gap: "8px", padding: "24px 40px", circle: 36, glyph: 18, titleSize: 13, titleLineHeight: "1.5", retryGlyph: 14, retryGap: "4px" },
  compact: { gap: "6px", padding: "16px 12px", circle: 28, glyph: 14, titleSize: 12, titleLineHeight: "18px", retryGlyph: 12, retryGap: "0px" },
};

const TONE_STYLE: Record<StateSlotTone, { background: string; color: string }> = {
  error: { background: ERROR_SURFACE, color: COLOR_CONSTANTS.error.main },
  info: { background: INFO_SURFACE, color: COLOR_CONSTANTS.primary.main },
  neutral: { background: NEUTRAL_SURFACE, color: COLOR_CONSTANTS.text.secondary },
};

/**
 * 목록·카드 영역을 채우는 상태 칸(조회 실패·빈 결과·권한 없음).
 * 놓인 영역의 크기를 그대로 쓰므로 갈래가 바뀌어도 틀이 움직이지 않는다.
 */
export const StateSlot: FC<StateSlotProps> = ({
  tone,
  icon,
  title,
  description,
  retryLabel,
  onRetry,
  density = "regular",
}) => {
  const toneStyle = TONE_STYLE[tone];
  const densityStyle = DENSITY_STYLE[density];
  const showsRetry = retryLabel !== undefined && onRetry !== undefined;

  return (
    <Box
      role={tone === "error" ? "alert" : undefined}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: densityStyle.gap,
        padding: densityStyle.padding,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: densityStyle.circle,
          height: densityStyle.circle,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: toneStyle.background,
          color: toneStyle.color,
        }}
      >
        <LineGlyph name={icon} size={densityStyle.glyph} />
      </Box>
      <Typography
        sx={{
          fontSize: densityStyle.titleSize,
          fontWeight: 600,
          lineHeight: densityStyle.titleLineHeight,
          color: COLOR_CONSTANTS.text.primary,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: CAPTION, marginTop: "-4px" }}>
          {description}
        </Typography>
      )}
      {showsRetry && (
        <Button
          size="small"
          emphasis="outlined"
          intent="inherit"
          onClick={onRetry}
          startIcon={<LineGlyph name="refresh" size={densityStyle.retryGlyph} />}
          sx={{ marginTop: densityStyle.retryGap }}
        >
          {retryLabel}
        </Button>
      )}
    </Box>
  );
};
